import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { EasingType, Keyframe, Track } from "../../types/timeline";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";
import { findClip } from "./clip-helpers";

/**
 * keyframe/setAll is absent on purpose: bulk-set.ts already owns it as a
 * generic clip-field handler, which is also what made the legacy validator's
 * early return for it dead code, since the validator asks the registry first.
 *
 * The three types here address a keyframe by (property, time) rather than by
 * its id, and nothing dedupes that pair, so a remove takes out every keyframe
 * sharing it and an add can mint a second one. The behaviour is set-shaped
 * where the inspector is single-keyframe-shaped. That is what the legacy
 * switches did, so the tests pin it rather than repair it.
 */

type KeyframeTarget = {
  readonly clipId?: unknown;
  readonly property?: unknown;
  readonly time?: unknown;
};

type AddParams = {
  readonly clipId: string;
  readonly property: string;
  readonly time: number;
  readonly value: unknown;
  readonly keyframeId?: string;
};

type TargetParams = {
  readonly clipId: string;
  readonly property: string;
  readonly time: number;
};

type UpdateParams = {
  readonly clipId: string;
  readonly property: string;
  readonly time: number;
  readonly value?: unknown;
  readonly easing?: EasingType;
};

function findTrackById(project: Project, trackId: string): Track | null {
  return project.timeline.tracks.find((t) => t.id === trackId) ?? null;
}

function mapClips(
  project: Project,
  fn: (clip: MutableClip) => MutableClip,
): void {
  const timeline = project.timeline as MutableTimeline;
  timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
    ...track,
    clips: track.clips.map(fn),
  }));
}

function validateKeyframe(action: Action, project: Project): ValidationResult {
  const errors: ValidationError[] = [];
  const params = action.params as KeyframeTarget;

  if (!params.clipId || typeof params.clipId !== "string") {
    errors.push({
      code: "INVALID_PARAMS",
      message: "Clip ID is required and must be a string",
      path: "params.clipId",
    });
    return { valid: false, errors };
  }

  const clip = findClip(project, params.clipId);
  if (!clip) {
    errors.push({
      code: "CLIP_NOT_FOUND",
      message: `Clip with ID ${params.clipId} not found`,
      path: "params.clipId",
    });
    return { valid: false, errors };
  }

  // The track comes from clip.trackId, not from a scan for the track holding
  // the clip the way transform.ts does it. The two disagree once a trackId has
  // gone stale, and this is the side the legacy validator was on.
  if (findTrackById(project, clip.trackId)?.locked) {
    errors.push({
      code: "TRACK_LOCKED",
      message: `Track containing clip ${params.clipId} is locked`,
      path: "params.clipId",
    });
  }

  if (!params.property || typeof params.property !== "string") {
    errors.push({
      code: "INVALID_PARAMS",
      message: "Property is required and must be a string",
      path: "params.property",
    });
  }

  if (typeof params.time !== "number" || params.time < 0) {
    errors.push({
      code: "INVALID_PARAMS",
      message: "Time is required and must be a non-negative number",
      path: "params.time",
    });
  }

  return { valid: errors.length === 0, errors };
}

function priorKeyframe(action: Action, projectBefore: Project): Keyframe | null {
  const params = action.params as TargetParams;
  const clip = findClip(projectBefore, params.clipId);
  if (!clip) return null;
  return (
    clip.keyframes.find(
      (kf) => kf.property === params.property && kf.time === params.time,
    ) ?? null
  );
}

const keyframeAdd: ActionHandler = {
  type: "keyframe/add",
  validate: validateKeyframe,

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as AddParams;
    const newKeyframe: Keyframe = {
      id: params.keyframeId ?? `keyframe-${crypto.randomUUID()}`,
      time: params.time,
      property: params.property,
      value: params.value,
      easing: "linear",
    };
    // Redo replays this same action object; without this it would mint a
    // fresh id and the stored inverse would target the one from the first run.
    (action.params as Record<string, unknown>).keyframeId = newKeyframe.id;

    mapClips(project, (clip) =>
      clip.id === params.clipId
        ? { ...clip, keyframes: [...clip.keyframes, newKeyframe] }
        : clip,
    );
    ctx.lastAddedIds.set("keyframe", newKeyframe.id);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as AddParams;
    // Nothing is read off the clip, but the legacy generator guarded all three
    // keyframe cases with this lookup before its switch, so an add against a
    // clip that is gone still has to come back uninvertible.
    if (!findClip(projectBefore, params.clipId)) return null;
    // Addressed by property and time, not by the id apply just stamped on the
    // action, so this undo also takes out any keyframe that already shared that
    // pair. Kept as it was; see the note at the top of the file.
    return createInverseAction(action, "keyframe/remove", {
      clipId: params.clipId,
      property: params.property,
      time: params.time,
    });
  },
};

const keyframeRemove: ActionHandler = {
  type: "keyframe/remove",
  validate: validateKeyframe,

  apply(action: Action, project: Project): void {
    const params = action.params as TargetParams;
    mapClips(project, (clip) =>
      clip.id === params.clipId
        ? {
            ...clip,
            keyframes: clip.keyframes.filter(
              (kf) =>
                !(kf.property === params.property && kf.time === params.time),
            ),
          }
        : clip,
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const keyframe = priorKeyframe(action, projectBefore);
    if (!keyframe) return null;
    const params = action.params as TargetParams;
    // Value only. The id and the easing are not carried, so undoing a removal
    // rebuilds the keyframe with a new id, linear easing and a position at the
    // end of the array. Legacy behaviour, recorded in the tests.
    return createInverseAction(action, "keyframe/add", {
      clipId: params.clipId,
      property: keyframe.property,
      time: keyframe.time,
      value: keyframe.value,
    });
  },
};

const keyframeUpdate: ActionHandler = {
  type: "keyframe/update",
  validate: validateKeyframe,

  apply(action: Action, project: Project): void {
    const params = action.params as UpdateParams;
    mapClips(project, (clip) =>
      clip.id === params.clipId
        ? {
            ...clip,
            keyframes: clip.keyframes.map((kf) =>
              kf.property === params.property && kf.time === params.time
                ? {
                    ...kf,
                    ...(params.value !== undefined && { value: params.value }),
                    ...(params.easing !== undefined && {
                      easing: params.easing,
                    }),
                  }
                : kf,
            ),
          }
        : clip,
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const keyframe = priorKeyframe(action, projectBefore);
    if (!keyframe) return null;
    const params = action.params as UpdateParams;
    return createInverseAction(action, "keyframe/update", {
      clipId: params.clipId,
      property: keyframe.property,
      time: keyframe.time,
      value: keyframe.value,
      easing: keyframe.easing,
    });
  },
};

for (const handler of [keyframeAdd, keyframeRemove, keyframeUpdate]) {
  registerActionHandler(handler);
}
